/**
 * Client-safe types + applicability filters for PUBLIC discount codes
 * (booking_addons rows with is_public = true), advertised on the /book
 * surfaces. Server loading lives in ./server/load-public-discount-codes.ts;
 * this module only narrows the already-loaded list per surface so location
 * or package switches never need a refetch.
 *
 * These filters are display-level heuristics only — redemption is always
 * re-validated server-side (validate_discount_code / redeem_discount_code),
 * so over-showing a code merely yields a friendly "invalid" on apply.
 */

export interface PublicDiscountCode {
  id: string;
  code: string;
  name: string;
  discount_type: 'percent' | 'fixed';
  discount_value: number;
  /** NULL = both booking surfaces; 'slot' | 'package' | 'product'. */
  applies_to: 'slot' | 'package' | 'product' | null;
  /** NULL = any activity category. */
  scope_package_type: string | null;
  /** Legacy singleton, used when scope_package_ids is absent or NULL. */
  scope_package_id: string | null;
  /** Empty = any package; nonempty = only these booking_packages rows. */
  scope_package_ids?: string[] | null;
  /** NULL = any template (slot surface narrowing). */
  scope_template_id: string | null;
  applicable_days_of_week: number[] | null;
  applicable_hours_start: string | null;
  applicable_hours_end: string | null;
  /** Empty = redeemable at all locations. */
  location_ids: string[];
  valid_until: string | null;
  booking_date_from?: string | null;
  booking_date_until?: string | null;
}

/** Codes redeemable when purchasing this specific package. */
export function publicCodesForPackage(
  codes: PublicDiscountCode[],
  pkg: {
    id: string;
    package_type?: string | null;
    location_id?: string | null;
  },
): PublicDiscountCode[] {
  return codes.filter((c) => {
    if (c.applies_to === 'slot' || c.applies_to === 'product') return false;
    if (
      c.scope_template_id !== null ||
      c.booking_date_from != null ||
      c.booking_date_until != null ||
      (c.applicable_days_of_week?.length ?? 0) > 0 ||
      c.applicable_hours_start !== null ||
      c.applicable_hours_end !== null
    )
      return false;
    const packageIds =
      c.scope_package_ids ?? (c.scope_package_id ? [c.scope_package_id] : []);
    if (packageIds.length > 0 && !packageIds.includes(pkg.id)) return false;
    if (
      c.scope_package_type &&
      pkg.package_type &&
      c.scope_package_type !== pkg.package_type
    )
      return false;
    if (
      c.location_ids.length > 0 &&
      (!pkg.location_id || !c.location_ids.includes(pkg.location_id))
    )
      return false;
    return true;
  });
}

/** Codes redeemable on the single-booking (slot / class) surface. */
export function publicCodesForSlotSurface(
  codes: PublicDiscountCode[],
  ctx: {
    bookingType?: string | null;
    templateId?: string | null;
    locationId?: string | null;
    bookingDate?: string | null;
  } = {},
): PublicDiscountCode[] {
  return codes.filter((c) => {
    if (c.applies_to === 'package' || c.applies_to === 'product') return false;
    if (c.booking_date_from || c.booking_date_until) {
      if (!ctx.bookingDate) return false;
      if (c.booking_date_from && ctx.bookingDate < c.booking_date_from) return false;
      if (c.booking_date_until && ctx.bookingDate > c.booking_date_until) return false;
    }
    const packageIds =
      c.scope_package_ids ?? (c.scope_package_id ? [c.scope_package_id] : []);
    if (packageIds.length > 0) return false;
    if (
      c.scope_package_type &&
      ctx.bookingType &&
      c.scope_package_type !== ctx.bookingType
    )
      return false;
    if (c.scope_template_id && ctx.templateId && c.scope_template_id !== ctx.templateId)
      return false;
    if (
      c.location_ids.length > 0 &&
      ctx.locationId &&
      !c.location_ids.includes(ctx.locationId)
    )
      return false;
    return true;
  });
}
